import React, { useState, useEffect } from "react"
import {
  Box,
  Button,
  Text,
  Table,
  Tbody,
  Tr,
  Td,
  Thead,
  Th,
  Alert,
  AlertIcon,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from "@chakra-ui/react"
import { AccountService } from "../api" // Adjust the import path as necessary
import { useAppContext } from "../AppContext" // Import the context

export const PublicKeysPage: React.FC = () => {
  const { wallets, setWallets } = useAppContext()
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [isDeleteModalOpen, setDeleteModalOpen] = useState(false)
  const [walletToDelete, setWalletToDelete] = useState<{ identifier: string; publicKey: string } | null>(null)

  // Fetch public keys only once when the component mounts
  useEffect(() => {
    const fetchPublicKeys = async () => {
      if (!wallets) {
        setLoading(true)
        setError(null) // Reset error state
        try {
          const keys = await AccountService.getAllPublicKeys()
          setWallets(keys) // Update context state
        } catch (err) {
          console.error("Error fetching public keys:", (err as Error).message)
          setError("Failed to fetch public keys.")
        } finally {
          setLoading(false)
        }
      }
    }

    fetchPublicKeys()
  }, [wallets, setWallets]) // Only fetch if wallets are null

  const openDeleteModal = (identifier: string, publicKey: string) => {
    setWalletToDelete({ identifier, publicKey })
    setDeleteModalOpen(true)
  }

  const handleDeleteWallet = async () => {
    if (!walletToDelete) return // Safety check
    setLoading(true)
    try {
      await AccountService.removeKeypair(walletToDelete.identifier)
      setWallets(prevKeys => {
        if (prevKeys) {
          const newKeys = { ...prevKeys }
          delete newKeys[walletToDelete.identifier] // Remove the deleted wallet from state
          return newKeys
        }
        return prevKeys
      })
      setDeleteModalOpen(false) // Close modal after deletion
    } catch (err) {
      console.error("Error deleting wallet:", (err as Error).message)
      setError("Failed to delete wallet.")
    } finally {
      setLoading(false)
    }
  }

  const formatPublicKey = (publicKey: string) => {
    // Hide the middle part of the public key
    if (publicKey.length > 20) {
      return `${publicKey.slice(0, 10)}...${publicKey.slice(-10)}`
    }
    return publicKey
  }

  return (
    <Box p={5}>
      <Text fontSize="2xl" mb={4}>
        Retrieve All Public Keys
      </Text>
      <Button colorScheme="teal" onClick={() => {}} isLoading={loading}>
        Refresh Public Keys
      </Button>

      {error && (
        <Alert status="error" mt={4}>
          <AlertIcon />
          {error}
        </Alert>
      )}

      {wallets && (
        <Box mt={4}>
          <Table variant="simple">
            <Thead>
              <Tr>
                <Th>Identifier</Th>
                <Th>Public Key</Th>
                <Th>Balance</Th>
                <Th>Actions</Th>
              </Tr>
            </Thead>
            <Tbody>
              {Object.entries(wallets).map(([identifier, { publicKey, balance }]) => (
                <Tr key={identifier}>
                  <Td>{identifier}</Td>
                  <Td>{formatPublicKey(publicKey)}</Td>
                  <Td>{balance}</Td>
                  <Td>
                    <Button colorScheme="red" onClick={() => openDeleteModal(identifier, publicKey)}>
                      Delete
                    </Button>
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        </Box>
      )}

      {/* Confirmation Modal for Deletion */}
      <Modal isOpen={isDeleteModalOpen} onClose={() => setDeleteModalOpen(false)} size="md">
        <ModalOverlay />
        <ModalContent width="400px">
          <ModalHeader>Confirm Deletion</ModalHeader>
          <ModalBody>
            {walletToDelete && (
              <Table variant="simple">
                <Thead>
                  <Tr>
                    <Th>Detail</Th>
                    <Th>Value</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  <Tr>
                    <Td>Identifier</Td>
                    <Td>{walletToDelete.identifier}</Td>
                  </Tr>
                  <Tr>
                    <Td>Public Key</Td>
                    <Td>{formatPublicKey(walletToDelete.publicKey)}</Td>
                  </Tr>
                </Tbody>
              </Table>
            )}
            <Text mt={4}>Are you sure you want to delete this wallet?</Text>
          </ModalBody>
          <ModalFooter>
            <Button colorScheme="blue" onClick={() => setDeleteModalOpen(false)}>
              Cancel
            </Button>
            <Button colorScheme="red" onClick={handleDeleteWallet} ml={3}>
              Delete
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  )
}
